import type { GitReference } from "@/hooks/use-git-reference";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import type { PlainProject } from "@/queries/project-queries";
import type { EffectiveState } from "@/routes/environments/phase";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import {
    EnvironmentPhase,
    GitInitializer_CloneTargetMode,
    type Environment,
    type EnvironmentInitializer,
} from "gitpod-next-api/gitpod/v1/environment_pb";

export function statusText(state?: EffectiveState): string | undefined {
    if (!state) {
        return;
    }

    let text = "unknown";

    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            text = "running";
            break;
        case EnvironmentPhase.CREATING:
            text = "starting";
            break;
        case EnvironmentPhase.STARTING:
            if (state.resuming) {
                text = "resuming";
            } else {
                text = "starting";
            }
            break;
        case EnvironmentPhase.UPDATING:
            text = "updating";
            break;
        case EnvironmentPhase.STOPPING:
            text = "stopping";
            if (state.timeout) {
                text = "auto-stopping";
            }
            break;
        case EnvironmentPhase.STOPPED:
            text = "stopped";
            if (state.timeout) {
                text = "auto-stopped";
            }
            break;
        case EnvironmentPhase.DELETING:
            text = "deleting";
            break;
        case EnvironmentPhase.DELETED:
            text = "deleted";
            break;
    }

    return `Environment ${text}`;
}

export function environmentLogsUrl(environment?: PlainEnvironment) {
    if (!environment?.status?.environmentUrls?.logs) {
        return;
    }
    // We're adding the status machine session to the URL to trigger a refresh when a new machine session starts
    // The macOS runner resets the log stream, so this ensures we clear our logs as well.
    return `${environment?.status?.environmentUrls?.logs}#${environment?.status?.machine?.session}`;
}

export function environmentLogGroupUrl(environmentId?: string, logGroupId?: string) {
    return `/details/${environmentId}/logs${logGroupId ? `#${logGroupId}` : ""}`;
}

/**
 * Extracts the git reference (branch or tag) from the environment initializer.
 *
 * The environment initializer can contain multiple initialization specs, each with a different type of context.
 * This function searches through the specs and returns the git reference from the first spec that has a "contextUrl" or "git" case.
 *
 * @param initializer The environment initializer object.
 * @returns The git reference object with type and value, or undefined if no reference could be found.
 */
export function getGitReferenceFromInitializer(
    initializer: PlainMessage<EnvironmentInitializer> | undefined,
): GitReference | undefined {
    if (!initializer?.specs || initializer.specs.length === 0) {
        return { type: "branch", value: "main" }; // default branch for empty initializer
    }

    for (const spec of initializer.specs) {
        switch (spec.spec.case) {
            case "contextUrl": {
                const reference = extractReferenceFromUrl(spec.spec.value.url);
                if (reference) return reference;
                break;
            }
            case "git": {
                const gitSpec = spec.spec.value;
                if (gitSpec.targetMode === GitInitializer_CloneTargetMode.REMOTE_TAG) {
                    return { type: "tag", value: gitSpec.cloneTarget };
                } else if (gitSpec.cloneTarget) {
                    return { type: "branch", value: gitSpec.cloneTarget };
                }
                break;
            }
        }
    }

    return undefined;
}

/**
 * Extracts the git branch from the environment initializer.
 *
 * @deprecated Use getGitReferenceFromInitializer instead for better tag support
 * @param initializer The environment initializer object.
 * @returns The repository URL, or undefined if no URL could be found.
 */
export function getBranchFromInitializer(initializer: PlainMessage<EnvironmentInitializer> | undefined) {
    const reference = getGitReferenceFromInitializer(initializer);
    return reference?.value || "main";
}

/**
 * Extracts the git reference (branch or tag) from a URL.
 *
 * @param url The repository URL to parse
 * @returns GitReference object with type and value, or undefined if no reference found
 */
export function extractReferenceFromUrl(url: string): GitReference | undefined {
    // Handle GitHub releases/tag URLs
    if (url.includes("/releases/tag/")) {
        const tagRegex = /\/releases\/tag\/([^/?#]+)/;
        const tagMatch = tagRegex.exec(url);
        if (tagMatch) {
            return { type: "tag", value: tagMatch[1] };
        }
    }

    // Handle tree URLs (branches)
    const treeParts = url.split("/tree/");
    if (treeParts.length > 1) {
        const branch = treeParts.pop()?.replace(/\?.*/, "");
        if (branch) {
            return { type: "branch", value: branch };
        }
    }

    // Default to default branch if no specific reference found
    return { type: "branch", value: "default branch" };
}

export function isBlankEnvironment(environment: PlainMessage<Environment> | undefined) {
    return environment?.spec?.content?.initializer?.specs?.length === 0;
}

export type GroupedArchivedEnvironments = {
    oneToTwoWeeks: ArchivedEnvironment[];
    twoWeeksToMonth: ArchivedEnvironment[];
    olderThanMonth: ArchivedEnvironment[];
};

export const groupArchivedEnvironmentsByArchiveDate = (
    environments: ArchivedEnvironment[],
): GroupedArchivedEnvironments => {
    const groupedEnvironments: GroupedArchivedEnvironments = {
        oneToTwoWeeks: [],
        twoWeeksToMonth: [],
        olderThanMonth: [],
    };

    if (environments.length > 0) {
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        oneWeekAgo.setMilliseconds(0);
        const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        twoWeeksAgo.setMilliseconds(0);
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        oneMonthAgo.setMilliseconds(0);

        environments.forEach((env) => {
            const archivedAt = env.environment.metadata?.archivedAt
                ? new Timestamp(env.environment.metadata?.archivedAt).toDate()
                : undefined;
            if (!archivedAt) return;

            archivedAt.setMilliseconds(0);

            if (archivedAt > twoWeeksAgo) {
                groupedEnvironments.oneToTwoWeeks.push(env);
            } else if (archivedAt <= twoWeeksAgo && archivedAt > oneMonthAgo) {
                groupedEnvironments.twoWeeksToMonth.push(env);
            } else if (archivedAt <= oneMonthAgo) {
                groupedEnvironments.olderThanMonth.push(env);
            }
        });

        const sortByArchivedAt = (a: ArchivedEnvironment, b: ArchivedEnvironment) => {
            const aTime = new Timestamp(a.environment.metadata?.archivedAt).toDate().getTime();
            const bTime = new Timestamp(b.environment.metadata?.archivedAt).toDate().getTime();
            return bTime - aTime;
        };

        groupedEnvironments.oneToTwoWeeks.sort(sortByArchivedAt);
        groupedEnvironments.twoWeeksToMonth.sort(sortByArchivedAt);
        groupedEnvironments.olderThanMonth.sort(sortByArchivedAt);
    }

    return groupedEnvironments;
};

export type ArchivedEnvironment = {
    environment: PlainEnvironment;
    projectId?: string;
    repoFullName?: string;
};

export function environmentsByProjectsToArchivedEnvironments(
    environmentsByProjects: {
        environments: PlainEnvironment[];
        projectId?: string;
        repoFullName?: string;
    }[],
): ArchivedEnvironment[] {
    const archivedEnvironments: ArchivedEnvironment[] = [];

    environmentsByProjects.forEach((group) => {
        group.environments.forEach((env) => {
            archivedEnvironments.push({
                environment: env,
                projectId: group.projectId,
                repoFullName: group.repoFullName,
            });
        });
    });

    return archivedEnvironments;
}

/**
 * Gets the display name for an environment using the same logic as the main sidebar.
 * Priority: custom name > agent execution name > branch name
 */
export function getEnvironmentDisplayName(environment: PlainEnvironment, agentExecution?: PlainAgentExecution): string {
    // 1. Custom environment name has highest priority
    if (environment.metadata?.name) {
        return environment.metadata.name;
    }

    // 2. Agent execution name for AI conversations
    if (agentExecution) {
        return agentExecution.metadata?.name || "New Conversation";
    }

    // 3. Branch name as fallback - use the same logic as getBranch
    if (environment.status?.content?.git?.branch) {
        return environment.status.content.git.branch;
    }

    // If the branch is not available and there are failure messages, then default to "no branch"
    if ((environment.status?.failureMessage?.length ?? 0) > 0) {
        return "no branch";
    }

    // Fall back to get the branch from the initializer
    return getBranchFromInitializer(environment.spec?.content?.initializer) || "";
}

/**
 * Gets the display name for a project using consistent logic.
 */
export function getProjectDisplayName(project?: PlainProject, projectMissing?: boolean): string {
    if (projectMissing) {
        return "Untitled project";
    }
    return project?.metadata?.name || "Untitled project";
}
